import { useCallback, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

/**
 * A dashboard panel the operator can fold away.
 *
 * The collapsed/expanded state is remembered per card in localStorage, so a
 * card someone has deliberately folded stays folded across reloads and shift
 * changes — the point of collapsing a standing list is not having to do it
 * again every morning.
 *
 * The header stays visible while collapsed and keeps its count, so folding a
 * card hides the detail without hiding the fact that something is wrong.
 */
export interface CollapsibleCardProps {
  /** Stable key for remembering the state. Changing it resets the memory. */
  storageKey: string;
  title: React.ReactNode;
  /** Rendered at the far side of the header, e.g. a caption or a link. */
  headerExtra?: React.ReactNode;
  /** Wrapper classes for the whole card (border, background). */
  className?: string;
  /** Classes for the header strip. */
  headerClassName?: string;
  /** Classes for the body wrapper. */
  bodyClassName?: string;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}

const readStored = (key: string, fallback: boolean): boolean => {
  try {
    const v = localStorage.getItem(`card-collapsed:${key}`);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
};

export default function CollapsibleCard({
  storageKey,
  title,
  headerExtra,
  className,
  headerClassName,
  bodyClassName,
  defaultCollapsed = false,
  children,
}: CollapsibleCardProps) {
  const [collapsed, setCollapsed] = useState(() => readStored(storageKey, defaultCollapsed));

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(`card-collapsed:${storageKey}`, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  return (
    <div className={clsx('rounded-xl overflow-hidden', className)}>
      <div className={clsx('flex items-center justify-between gap-2 flex-wrap', headerClassName)}>
        {/* The whole title area is the toggle, so the hit target is large;
            headerExtra sits outside it because it often holds its own link. */}
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          className="flex items-center gap-2 text-right min-w-0 flex-1"
        >
          <ChevronDownIcon
            className={clsx('w-5 h-5 shrink-0 transition-transform', collapsed && '-rotate-90')}
          />
          <span className="min-w-0">{title}</span>
        </button>
        {headerExtra}
      </div>
      {!collapsed && <div className={bodyClassName}>{children}</div>}
    </div>
  );
}
